import React from 'react';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import ReviewsIsEmpty from './ReviewsIsEmpty';
import RatingStarsInline from './RatingStarsInline';
import styles from './Reviews.module.scss';

export type ReviewRow = {
    id: string;
    bookingId: number;
    orderId: number;
    client: string;
    pet: string;
    date: string;
    staff: string;
    rating: number;
    review: string;
    tags?: string[];
};

interface ReviewsTableProps {
    rows: ReviewRow[];
    isLoading?: boolean;
    className?: string;
    pagination?: {
        page: number;
        totalPages: number;
        size?: number;
    };
    onPageChange?: (page: number) => void;
    onSizeChange?: (size: number) => void;
    onViewBooking?: (bookingId: number, orderId: number) => void;
}

const ReviewsTable: React.FC<ReviewsTableProps> = ({
    rows,
    isLoading,
    className,
    pagination,
    onPageChange,
    onSizeChange,
    onViewBooking
}) => {
    const toTitle = (s: string) =>
        s
            .replace(/[-_]+/g, ' ')
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());

    const renderReviewText = (r: ReviewRow): string => {
        // 4–5 stars: show tags as comma-separated text; if no tags -> dash
        if (r.rating >= 4) {
            const tags = Array.isArray(r.tags) ? r.tags : [];
            if (tags.length > 0) {
                return tags.map(toTitle).join(', ');
            }
            return '—';
        }
        // 1–3 stars: show comment text; if empty -> dash
        const txt = (r.review || '').trim();
        return txt || '—';
    };
    return rows.length > 0 ? (
        <Table
            className={className}
            pagination={pagination}
            onPageChange={onPageChange}
            onSizeChange={onSizeChange}
        >
            <TableHeader>
                <TableHeaderCell>Client</TableHeaderCell>
                <TableHeaderCell>Pet</TableHeaderCell>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Staff</TableHeaderCell>
                <TableHeaderCell>Rating</TableHeaderCell>
                <TableHeaderCell>Review</TableHeaderCell>
                <TableHeaderCell />
            </TableHeader>
            <TableBody>
                {rows.map(r => (
                    <TableRow key={r.id}>
                        <TableCell>{r.client}</TableCell>
                        <TableCell>{r.pet}</TableCell>
                        <TableCell>{r.date}</TableCell>
                        <TableCell>{r.staff}</TableCell>
                        <TableCell>
                            <RatingStarsInline value={r.rating} />
                        </TableCell>
                        <TableCell>
                            <span className={styles.reviewText}>{renderReviewText(r)}</span>
                        </TableCell>
                        <TableCell isActions>
                            <div className={styles.actionsContainer}>
                                <button
                                    type="button"
                                    title="View Booking"
                                    className={styles.viewBookingLink}
                                    onClick={onViewBooking ? () => onViewBooking(r.bookingId, r.orderId) : undefined}
                                >
                                    View Booking
                                </button>
                            </div>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    ) : (
        <Table
            className={className}
            pagination={pagination}
            onPageChange={onPageChange}
            onSizeChange={onSizeChange}
        >
            <TableBody>
                <tr>
                    <td
                        colSpan={7}
                        className={styles.emptyCell}
                    >
                        <div className={styles.emptyCellContent}>
                            <ReviewsIsEmpty
                                title="No reviews yet"
                                text="Once someone leaves a review, it will appear here."
                            />
                        </div>
                    </td>
                </tr>
            </TableBody>
        </Table>
    );
};

export default ReviewsTable;
